// WP-1.1 (M5, AC-1..6, S-11/S-12): account queries/mutations over the engine-backed API.
import {
  accountListResponseSchema,
  accountResponseSchema,
  type AccountCreateRequest,
  type AccountEditRequest,
  type AccountResponse,
} from '@pfos/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from './api';

export const ACCOUNTS_KEY = ['accounts'] as const;
export const accountKey = (id: string): readonly [string, string] => ['accounts', id] as const;

export function useAccounts(): UseQueryResult<AccountResponse[]> {
  return useQuery({
    queryKey: ACCOUNTS_KEY,
    queryFn: async () => {
      const body = accountListResponseSchema.parse(await api.get('/api/v1/accounts'));
      return body.accounts;
    },
  });
}

export function useAccount(id: string): UseQueryResult<AccountResponse> {
  return useQuery({
    queryKey: accountKey(id),
    queryFn: async () => accountResponseSchema.parse(await api.get(`/api/v1/accounts/${id}`)),
  });
}

function useInvalidateAccounts(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: ACCOUNTS_KEY });
  };
}

export function useCreateAccount(): UseMutationResult<
  AccountResponse,
  Error,
  AccountCreateRequest
> {
  const invalidate = useInvalidateAccounts();
  return useMutation({
    mutationFn: async (input: AccountCreateRequest) =>
      accountResponseSchema.parse(await api.post('/api/v1/accounts', input)),
    onSuccess: invalidate,
  });
}

export function useEditAccount(
  id: string,
): UseMutationResult<AccountResponse, Error, AccountEditRequest> {
  const invalidate = useInvalidateAccounts();
  return useMutation({
    mutationFn: async (patch: AccountEditRequest) =>
      accountResponseSchema.parse(await api.patch(`/api/v1/accounts/${id}`, patch)),
    onSuccess: invalidate,
  });
}

export function useArchiveAccount(id: string): UseMutationResult<AccountResponse, Error, void> {
  const invalidate = useInvalidateAccounts();
  return useMutation({
    mutationFn: async () =>
      accountResponseSchema.parse(await api.post(`/api/v1/accounts/${id}/archive`)),
    onSuccess: invalidate,
  });
}

export function useUnarchiveAccount(id: string): UseMutationResult<AccountResponse, Error, void> {
  const invalidate = useInvalidateAccounts();
  return useMutation({
    mutationFn: async () =>
      accountResponseSchema.parse(await api.post(`/api/v1/accounts/${id}/unarchive`)),
    onSuccess: invalidate,
  });
}
